// Споделени типове, константи и чисти помощници за работния лист „Изписване"
// (страниците за създаване и редакция). UI частите са в WriteoffWorksheet*.tsx
// и WriteoffLineCards.tsx, логиката на редовете — в useWriteoffRowsActions.ts.
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { TFunction } from "i18next";
import type { Lot, Product, StockAsOf, Writeoff } from "../../../../api/inventory";
import { clampDecimals, formatQty } from "../../components/qty";
import { formatQtyInput, parseDecimal } from "../../components/numbers";

// Числовите формати са общи за склада (components/numbers) — тук остават
// реекспортирани, за да не се пипат всички вносители наведнъж.
export { formatMoney, formatQtyInput, formatUnitPrice, parseDecimal } from "../../components/numbers";

export type AddKind = "product" | "recipe";
export type WriteoffKind = "writeoff" | "scrap";

// Предефинирани причини за БРАКУВАНЕ (стойността се пази на BG — канонична, за
// стабилно филтриране; етикетът се локализира). Задължителни при вид „Бракуване".
export const WRITEOFF_REASONS: { value: string; key: string }[] = [
  { value: "Изтекъл срок", key: "scrapReasonExpired" },
  { value: "Развалена стока", key: "scrapReasonSpoiled" },
  { value: "Счупен/разсипан", key: "scrapReasonBroken" },
  { value: "Несъответствие", key: "scrapReasonMismatch" },
];

// ЗАПАЗЕНА причина на автоматичното бракуване при сторно с „брак" от Маркиране
// (BE: `SCRAP_REASON_VOID_MARKING` в `inventory/service.py` — кросрепо
// инвариант). Не се предлага при ръчно създаване; при редакция на такъв запис
// видът и причината са залостени, а проверката за наличност (FE и BE) се
// пропуска — записът е роден с разрешен минус.
export const AUTO_SCRAP_REASON = "Брак от Маркиране";

// Един резултат от търсачката / „Целия списък": продукт (с наличност) или рецепта.
export type WriteoffSearchResult =
  | {
      kind: "product";
      id: number;
      name: string;
      unit: string | null;
      available: number;
      decimals: number;
    }
  | { kind: "recipe"; id: number; name: string };

export type WriteoffLineDraft = {
  key: number;
  kind: AddKind; // продуктов ред или ред за рецепта
  productId: number | null;
  // Като в работния лист на доставките: количеството е ТЕКСТ (плоска клетка,
  // приема точка и запетая; форматира се на blur). За рецепта = порции.
  quantity: string;
  lotId: number | null; // null = FIFO
  note: string;
  // ред-рецепта (неразделена)
  recipeId: number | null;
  recipeName: string | null;
  // продуктови редове, получени от разделяне на рецепта (за „Върни в рецепта")
  groupId: number | null;
  groupRecipeId: number | null;
  groupRecipeName: string | null;
  groupPortions: number | null;
  groupNote: string | null;
};

export const emptyWriteoffLine = (
  key: number,
  kind: AddKind,
): WriteoffLineDraft => ({
  key,
  kind,
  productId: null,
  quantity: "",
  lotId: null,
  note: "",
  recipeId: null,
  recipeName: null,
  groupId: null,
  groupRecipeId: null,
  groupRecipeName: null,
  groupPortions: null,
  groupNote: null,
});

// Началното състояние на работния лист (create = празно; edit = от записа).
export type WriteoffWorksheetInitial = {
  date: Dayjs; // пази и часа — при редакция запазеният час остава
  warehouseId: number | null;
  kind: WriteoffKind;
  reason: string;
  comment: string;
  lines: WriteoffLineDraft[];
  nextKey: number;
  // редактираното изписване — наличността към датата се пита БЕЗ него
  // (`excludeMovementId`), както го сверява BE при запис
  editedWriteoffId?: number | null;
};

// Какво попълва бързото начало на началния екран в новия лист (router state
// `{preset}`): видът и причината за брак.
export type WriteoffPreset = { kind: WriteoffKind; reason: string };

export const emptyWorksheetInitial = (
  warehouseId: number | null,
): WriteoffWorksheetInitial => ({
  date: dayjs(),
  warehouseId,
  kind: "writeoff",
  reason: "",
  comment: "",
  lines: [],
  nextKey: 1,
});

// Редовете на записано изписване → чернови редове за редакция. Редовете от една
// рецепта (същият recipeId + порции) се сглобяват ОБРАТНО в един ред-рецепта, за
// да се запази кое е рецепта; останалите стават продуктови редове. decimalsOf
// дава знаците на мерната единица на продукт (за форматиране на количеството).
export const worksheetInitialFromWriteoff = (
  writeoff: Writeoff,
  decimalsOf: (productId: number) => number,
): WriteoffWorksheetInitial => {
  const lines: WriteoffLineDraft[] = [];
  const seenRecipeKeys = new Set<string>();
  let nextKey = 0;
  for (const line of writeoff.lines) {
    if (line.recipeId) {
      const recipeKey = `${line.recipeId}|${line.recipeQuantity ?? ""}`;
      if (!seenRecipeKeys.has(recipeKey)) {
        lines.push({
          ...emptyWriteoffLine(nextKey, "recipe"),
          recipeId: line.recipeId,
          recipeName: line.recipeName ?? null,
          quantity:
            line.recipeQuantity != null
              ? formatPortionsInput(line.recipeQuantity)
              : "",
          note: line.note ?? "",
        });
        seenRecipeKeys.add(recipeKey);
        nextKey += 1;
      }
      // иначе: вече представена от ред-рецепта (съставките са скрити)
    } else {
      lines.push({
        ...emptyWriteoffLine(nextKey, "product"),
        productId: line.productId,
        quantity: formatQtyInput(line.quantity, decimalsOf(line.productId)),
        note: line.note ?? "",
        // ед. цената винаги се преизчислява от партидата/FIFO (без ръчна стойност)
      });
      nextKey += 1;
    }
  }
  return {
    date: dayjs(writeoff.date),
    warehouseId: writeoff.warehouseId,
    kind: writeoff.kind ?? "writeoff",
    reason: writeoff.reason ?? "",
    comment: writeoff.comment ?? "",
    lines,
    nextKey: nextKey + 1,
    editedWriteoffId: writeoff.id,
  };
};

// Порции (множител на рецепта) — без излишни нули (2 → „2", 1.5 → „1,5").
export const formatPortionsInput = (value: number): string =>
  String(value).replace(".", ",");

// ─── Ед. цена на продуктов ред ──────────────────────────────────────────────
// Претеглена по количествата, които реално ще се консумират от всяка партида
// (същата сметка като бекенда): при избрана партида — първо тя, остатъкът по
// FIFO от другите (BE `replay._consumption_order`); иначе FIFO.
export const fifoUnitPrice = (
  lots: Lot[],
  quantity: number,
  lotId: number | null,
): number | null => {
  const chosenLot = lotId ? lots.find((lot) => lot.id === lotId) : undefined;
  if (lotId && !chosenLot) return null;
  const orderedLots = chosenLot
    ? [chosenLot, ...lots.filter((lot) => lot.id !== chosenLot.id)]
    : lots;
  if (quantity <= 0) return orderedLots.length ? orderedLots[0].unitPrice : null;
  let needed = quantity;
  let cost = 0;
  let consumed = 0;
  for (const lot of orderedLots) {
    if (needed <= 0) break;
    const taken = Math.min(lot.remaining, needed);
    cost += taken * lot.unitPrice;
    consumed += taken;
    needed -= taken;
  }
  if (consumed <= 0) return orderedLots.length ? orderedLots[0].unitPrice : null;
  return cost / consumed;
};

// Стойностите на продуктов ред за показване — едни и същи за решетката и за
// картите. Себестойността е ВИНАГИ нето; Бруто по регистрацията по ДДС на фирмата.
export const productLineValues = (
  line: WriteoffLineDraft,
  product: Product | null,
  asOf: StockAsOf | null,
  vatRegistered: boolean,
) => {
  const decimals = clampDecimals(product?.decimalPlaces);
  const unit = asOf?.unit ?? product?.unit ?? null;
  // null = наличността към датата още се зарежда
  const available = line.productId == null ? 0 : (asOf?.quantity ?? null);
  const lots = asOf?.lots ?? [];
  const quantity = parseDecimal(line.quantity);
  const unitPrice = fifoUnitPrice(lots, quantity, line.lotId);
  const netSum = quantity > 0 && unitPrice != null ? quantity * unitPrice : null;
  const vatRate = product?.vatRate ?? 0;
  const grossSum =
    netSum == null ? null : vatRegistered && vatRate ? netSum * (1 + vatRate / 100) : netSum;
  return { decimals, unit, available, lots, quantity, unitPrice, netSum, grossSum };
};

// Етикетът на партида: „Партида X · Ост. 2,000 кг · Срок 01.02.27" — срокът се
// показва винаги (дата или „без срок на годност").
export const lotLabelOf = (lot: Lot, decimals: number, unit: string | null, t: TFunction) =>
  [
    lot.batchNumber
      ? `${t("inventoryPage.batchShort")} ${lot.batchNumber}`
      : t("inventoryPage.noBatch"),
    `${t("inventoryPage.remainShort")} ${formatQty(lot.remaining, decimals)}${unit ? ` ${unit}` : ""}`,
    lot.expiryDate
      ? `${t("inventoryPage.expiryShort")} ` +
        new Date(lot.expiryDate).toLocaleDateString("bg-BG", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : t("inventoryPage.noExpiry"),
  ].join(" · ");

// ─── Оцветяване на съвпадналите думи в резултат на търсачката ───────────────
// Оцветяването на съвпаденията е ОБЩО за модула (един файл, три търсачки).
export { highlightWords } from "../../components/highlight";

// ─── Решетка на редовете ────────────────────────────────────────────────────
// # · Продукт/Рецепта · Количество · Партида · Ед. цена · Нето · Бруто ·
// Бележка · ✕. Както в доставката: ЕДИНСТВЕНО „Продукт" е гъвкава колона —
// останалите са фиксирани, а ✕ е тясна 40px.
export const LINE_GRID_CLASS =
  "grid grid-cols-[44px_minmax(220px,1.4fr)_110px_240px_100px_110px_110px_160px_40px]";
export const LINE_GRID_MIN_WIDTH_CLASS = "min-w-[1134px]";
// колоните на LINE_GRID_CLASS — за пълнежа над реда „Общо"
export const LINE_GRID_COLUMN_COUNT = 9;

// ─── Клавиатурна навигация в решетката ──────────────────────────────────────
// Фокусируемите клетки на ред: 0 Количество/Порции · 1 Партида · 2 Бележка
// (ред-рецепта няма „Партида" — стрелките я прескачат). Id-тата са
// `wox-{ред}-{колона}`, както ги очаква `components/gridNavigation`; картите под
// десктоп ширина носят СЪЩИТЕ id-та.
export const WRITEOFF_GRID_PREFIX = "wox";
export const WRITEOFF_COL_COUNT = 3;
export const cellId = (lineKey: number, col: number) =>
  `${WRITEOFF_GRID_PREFIX}-${lineKey}-${col}`;
// id на клетката „Количество" на ред (за фокус след добавяне от търсачката)
export const qtyCellId = (lineKey: number) => cellId(lineKey, 0);
